import { cn } from '@invent/wl-ui-kit-next';
import {
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { CheckIcon } from 'lucide-react';
import * as React from 'react';

import {
  holdsOrgImage,
  InlineField,
  KEEP_EDITING,
  ORG_IMAGE_READ_ONLY,
  RichText,
  RichTextInput,
  type IInlineControl,
  type InlineAnswer
} from '@/design';
import { ApiError } from '@/domains/api';
import type {
  IInternalFieldOption,
  IInternalValueOption,
  InternalFieldType,
  InternalPayload
} from '../api/internal-field-contracts';
import { NO_ANSWER } from '../answer-text';
import {
  decodeInternalValue,
  encodeInternalValue,
  internalMalformedHint,
  internalValueText,
  isMalformedInternal,
  sameInternalValue,
  type InternalUiValue
} from '../internal-field-values';
import { refusalMessage } from '../refusal';

/**
 * The eleven internal-field types as controls and as read values, and the one composition a record
 * page calls to edit one (the advisor record page's roadmap, K2; the F1 kit plan's R4 and R5).
 *
 * **One control per type, by survey-api's type and nothing else**: a text box for `text`, a text
 * area for `long_text`, the rich-text editor for `rich_text`, a text box typed for a keyboard for
 * the four numbers (`inputMode` only — no mask and no bound, survey-api judges), a date control, a
 * tick for `bool`, the kit's select for `select` and a list of ticks for `multi_select`, options by
 * their `ordinal`. **A choice offers live options only** — survey-api refuses a retired one for a
 * new value — and **a stored option no longer offered stays drawn**, labelled by the value's own
 * `options` and marked retired, so opening the field does not quietly change it: a select's is its
 * first item, disabled; a multi-select's is a tick of its own, said to be taken off before Save.
 *
 * **`AdminInlineField` is what a block calls** (F2's research fields): `InlineField` around the
 * control, with the payload decided here (`internal-field-values.ts`) and a refusal turned into
 * survey-api's words (`refusalMessage`) — the design system never sees an `ApiError` (R4). It
 * holds a value no payload can carry with the shape the field wants, sends nothing for a value
 * saved unchanged, and clears an emptied control through `clear` (a `DELETE`); a `bool` has no
 * clear, unticked being the value `false` (U4). A rich-text value holding an org image is
 * read-only until survey-api's block 8 has moved the images (R13), and what the editor cannot keep
 * is said above Save.
 */

/** What a field says for a write whose answer never arrived — the connection dropped, or
 *  survey-api failed with a 5xx: not a refusal, since the write may have landed (the hub's rule,
 *  `said.tsx`' `unanswered`). Shared with a block that draws `InlineField` itself (F6's rankings). */
export const NOT_ARRIVED =
  'The answer did not arrive — check whether the value changed before saving again.';

/** A field as a control needs it: its type, and a choice's options. */
export interface IInternalFieldShape {
  type: InternalFieldType;
  options?: readonly IInternalFieldOption[];
}

/** The select's "no value" item: a Radix item cannot carry `''`. */
const NONE = '__none__';

function liveOptions(options: readonly IInternalFieldOption[]): IInternalFieldOption[] {
  return options.filter((option) => !option.retired).sort((a, b) => a.ordinal - b.ordinal);
}

/** The control for one type, drawn by `InlineField` while editing. `stored` labels a stored code
 *  that is no longer offered. */
export function InternalFieldControl({
  field,
  stored = [],
  control,
  onLosses,
  dateInput
}: {
  field: IInternalFieldShape;
  stored?: readonly IInternalValueOption[];
  control: IInlineControl<InternalUiValue>;
  /** What the rich-text editor cannot keep, said above Save. */
  onLosses?: (notice: Nullable<string>) => void;
  /** The date control's element, whose `validity.badInput` says a date is half typed. */
  dateInput?: React.Ref<HTMLInputElement>;
}) {
  const { label, value, onChange, disabled } = control;
  const text = typeof value === 'string' ? value : '';

  switch (field.type) {
    case 'long_text':
      return (
        <Textarea
          aria-label={label}
          rows={4}
          value={text}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case 'rich_text':
      return (
        <RichTextInput
          label={label}
          value={text}
          disabled={disabled}
          onChange={onChange}
          onLosses={onLosses}
        />
      );
    case 'bool':
      return (
        <Checkbox
          aria-label={label}
          checked={value === true}
          disabled={disabled}
          onCheckedChange={(next) => onChange(next === true)}
        />
      );
    case 'select':
      return <SelectControl field={field} stored={stored} control={control} />;
    case 'multi_select':
      return <MultiSelectControl field={field} stored={stored} control={control} />;
    default:
      return (
        <Input
          ref={field.type === 'date' ? dateInput : undefined}
          aria-label={label}
          type={field.type === 'date' ? 'date' : 'text'}
          inputMode={inputModeOf(field.type)}
          value={text}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}

function inputModeOf(type: InternalFieldType): React.HTMLAttributes<HTMLInputElement>['inputMode'] {
  if (type === 'int') return 'numeric';
  if (type === 'decimal' || type === 'money' || type === 'percent') return 'decimal';
  return undefined;
}

function SelectControl({
  field,
  stored,
  control
}: {
  field: IInternalFieldShape;
  stored: readonly IInternalValueOption[];
  control: IInlineControl<InternalUiValue>;
}) {
  const live = liveOptions(field.options ?? []);
  const current = typeof control.value === 'string' ? control.value : '';
  const retired =
    current !== '' && !live.some((option) => option.code === current) ? current : null;

  return (
    <Select
      value={current === '' ? NONE : current}
      disabled={control.disabled}
      onValueChange={(next) => control.onChange(next === NONE ? '' : next)}
    >
      <SelectTrigger aria-label={control.label} className="w-full max-w-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {retired !== null && (
          <SelectItem value={retired} disabled>
            {labelOf(retired, field.options, stored)} — retired, choose another
          </SelectItem>
        )}
        <SelectItem value={NONE}>— None —</SelectItem>
        {live.map((option) => (
          <SelectItem key={option.code} value={option.code}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MultiSelectControl({
  field,
  stored,
  control
}: {
  field: IInternalFieldShape;
  stored: readonly IInternalValueOption[];
  control: IInlineControl<InternalUiValue>;
}) {
  const ticked = Array.isArray(control.value) ? control.value : [];
  const live = liveOptions(field.options ?? []);
  // A stored code no longer offered stays drawn while the edit lasts, its tick free both ways: a
  // tick taken off by mistake can be put back before Save, and none is disabled under the focus
  // that pressed it. survey-api refuses a write naming a retired option, so the label says it.
  const [kept] = React.useState(() =>
    ticked.filter((code) => !live.some((option) => option.code === code))
  );

  // A new tick goes last: survey-api keeps the order the options were chosen in.
  const toggle = (code: string, on: boolean) => {
    const others = ticked.filter((other) => other !== code);
    control.onChange(on ? [...others, code] : others);
  };

  const row = (code: string, text: string, retired: boolean) => {
    const on = ticked.includes(code);
    return (
      <label key={code} className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={on}
          disabled={control.disabled}
          onCheckedChange={(next) => toggle(code, next === true)}
        />
        <span className={cn(retired && 'text-muted-foreground')}>
          {text}
          {retired && ' — retired, untick it to save'}
        </span>
      </label>
    );
  };

  // No group of its own: the field's shell is already the group named "<label> for <name>", and a
  // second one by that name would make it ambiguous.
  return (
    <div className="grid gap-1.5">
      {kept.map((code) => row(code, labelOf(code, field.options, stored), true))}
      {live.map((option) => row(option.code, option.label, false))}
    </div>
  );
}

/** A code's label: the value's own (which survey-api resolves for a retired option), then the
 *  schema's, then the code itself. */
function labelOf(
  code: string,
  options: readonly IInternalFieldOption[] = [],
  stored: readonly IInternalValueOption[] = []
): string {
  return (
    stored.find((option) => option.code === code)?.label ??
    options.find((option) => option.code === code)?.label ??
    code
  );
}

/** A stored value as it reads, by type. */
export function InternalFieldDisplay({
  payload,
  options = []
}: {
  payload: Nullable<InternalPayload>;
  /** Labels for its codes — the value's own first, then the schema's. */
  options?: readonly IInternalValueOption[];
}) {
  if (!payload) return <span className="text-muted-foreground">{NO_ANSWER}</span>;
  if (payload.t === 'rich_text') return <RichText html={payload.v} />;
  if (payload.t === 'bool') {
    // A box that cannot be pressed: drawn, not a control.
    const label = internalValueText(payload);
    return (
      <span
        role="img"
        aria-label={label}
        className={cn(
          'inline-flex size-5 items-center justify-center rounded-md border-[1.5px]',
          payload.v ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
        )}
      >
        {payload.v && <CheckIcon className="size-3.5" aria-hidden />}
      </span>
    );
  }
  return <span className="whitespace-pre-line">{internalValueText(payload, options)}</span>;
}

/**
 * One internal field of a record, read and edited in place — `InlineField` with this type's
 * control, its payload and survey-api's refusals in words. `write` and `clear` are the block's
 * calls (`PUT` and `DELETE`), resolving once the page shows the result and throwing a refusal;
 * `refusal` turns one into a sentence where a route's statuses need their own (the default is
 * `refusalMessage`'s, `problems[]` included).
 *
 * **A `write` or `clear` that resolves `KEEP_EDITING` is handed to `InlineField` as is** — back to
 * editing with nothing said, the draft kept: a block that asks a question before writing (F5's
 * figures ask for a reason) and was told no. Any other resolution means the write landed.
 * `display`, where given, is the value as read in place of `InternalFieldDisplay`'s — a figure's
 * cell draws "Cleared" and its change beneath (F5's R3).
 */
export function AdminInlineField({
  field,
  name,
  value,
  valueOptions = [],
  readOnly,
  write,
  clear,
  refusal,
  actions,
  display,
  onOpenChange,
  notPermitted
}: {
  field: IInternalFieldShape & { code: string; label: string };
  /** Whose value it is. */
  name: string;
  value: Nullable<InternalPayload>;
  /** The stored value's labels for its codes. */
  valueOptions?: readonly IInternalValueOption[];
  readOnly?: { reason: string };
  write: (payload: InternalPayload) => Promise<unknown>;
  clear: () => Promise<unknown>;
  refusal?: (error: unknown) => string;
  actions?: React.ReactNode;
  /** The value as read, where the block draws it its own way. */
  display?: React.ReactNode;
  /** `InlineField`'s: told as the field opens and closes. */
  onOpenChange?: (open: boolean) => void;
  /** `InlineField`'s: the id of the `NotPermitted` sentence that draws the pencil off. */
  notPermitted?: string;
}) {
  const { type } = field;
  const [losses, setLosses] = React.useState<Nullable<string>>(null);
  const draftOf = React.useMemo(() => decodeInternalValue(value, type), [value, type]);
  const dateInput = React.useRef<HTMLInputElement>(null);

  // A half-typed date is held, not cleared: the date input hands over `''` for it, exactly as for
  // a date emptied on purpose, and only the element knows which — read at the press.
  const halfTypedDate = () => type === 'date' && dateInput.current?.validity.badInput === true;

  // An answer that never arrived is not a refusal: the field says to check rather than blaming
  // the value (`NOT_ARRIVED`).
  const say = (error: unknown): string => {
    if (!(error instanceof ApiError) || error.status >= 500) return NOT_ARRIVED;
    return refusal ? refusal(error) : refusalMessage(error, 'The value could not be saved.');
  };

  // `InlineField`'s contract: `null` when the write landed, `KEEP_EDITING` when the block was told
  // not to send it, else the sentence to say.
  const settle = async (send: () => Promise<unknown>): Promise<InlineAnswer> => {
    try {
      return (await send()) === KEEP_EDITING ? KEEP_EDITING : null;
    } catch (error) {
      return say(error);
    }
  };

  const orgImage = value?.t === 'rich_text' && holdsOrgImage(value.v);

  return (
    <InlineField<InternalUiValue>
      label={field.label}
      name={name}
      fieldKey={field.code}
      display={
        display ?? (
          <InternalFieldDisplay
            payload={value}
            options={[...valueOptions, ...(field.options ?? [])]}
          />
        )
      }
      value={draftOf}
      readOnly={readOnly ?? (orgImage ? { reason: ORG_IMAGE_READ_ONLY } : undefined)}
      multiline={type === 'long_text' || type === 'rich_text' || type === 'multi_select'}
      notice={type === 'rich_text' ? losses : undefined}
      actions={actions}
      onOpenChange={onOpenChange}
      notPermitted={notPermitted}
      isEmpty={(draft) =>
        type !== 'bool' &&
        !halfTypedDate() &&
        encodeInternalValue(type, draft) === null &&
        !isMalformedInternal(type, draft)
      }
      renderControl={(control) => (
        <InternalFieldControl
          field={field}
          stored={valueOptions}
          control={control}
          onLosses={setLosses}
          dateInput={dateInput}
        />
      )}
      onSave={async (draft) => {
        if (halfTypedDate() || isMalformedInternal(type, draft)) return internalMalformedHint(type);
        const payload = encodeInternalValue(type, draft);
        if (payload === null || sameInternalValue(payload, value)) return null;
        return settle(() => write(payload));
      }}
      onClear={type === 'bool' ? undefined : () => settle(clear)}
    />
  );
}
